import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { BrowserRouter } from 'react-router-dom';
import {
  A11yLabelsProvider,
  AnnouncerProvider,
  AppThemeProvider,
  ConfirmProvider,
  govTheme,
  MuiLocaleProvider,
  SnackbarProvider,
  useSnackbar,
} from '@munifocus/ui';
import { initI18n } from '@munifocus/i18n';
import { queryClient, setMutationErrorReporter } from '../lib/query-client';
import { apiErrorMessage } from '../lib/mutation-toast';
import { AuthProvider, useAuth } from './AuthProvider';
import { ScopeProvider } from './ScopeProvider';
import { RealtimeProvider } from '../lib/realtime';
import { AppErrorBoundary } from '../components/AppErrorBoundary';

const LOCALE_KEY = 'gov.locale';

export function AppProviders({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = (localStorage.getItem(LOCALE_KEY) as 'en' | 'fr' | null) ?? undefined;
    const def = (import.meta.env.VITE_DEFAULT_LOCALE as 'en' | 'fr' | undefined) ?? 'en';
    void initI18n({ lng: stored ?? def, fallbackLng: 'en' }).then(() => setReady(true));
  }, []);

  const i18n = useMemo(() => i18next, []);

  if (!ready) return null;

  return (
    <AppThemeProvider theme={govTheme} cacheKey="gov">
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <MuiLocaleProvider>
            <AppErrorBoundary>
              <LocalizedA11yLabels>
                <LocalizedSnackbarProvider>
                  <MutationErrorReporter />
                  <ConfirmProvider>
                    <AnnouncerProvider>
                      <BrowserRouter>
                        <AuthProvider>
                          {/* Inside AuthProvider: the active scope is a property of
                          the signed-in account, and the session probe it reads
                          is meaningless before one exists. */}
                          <ScopeProvider>
                            <AuthedRealtime>{children}</AuthedRealtime>
                          </ScopeProvider>
                        </AuthProvider>
                      </BrowserRouter>
                    </AnnouncerProvider>
                  </ConfirmProvider>
                </LocalizedSnackbarProvider>
              </LocalizedA11yLabels>
            </AppErrorBoundary>
          </MuiLocaleProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </AppThemeProvider>
  );
}

/**
 * Opens the realtime socket only once the session probe has resolved a user.
 *
 * Inside `AuthProvider` rather than around it, because it needs to know whether
 * anyone is signed in — and a socket opened on the login screen would be
 * rejected and retried for as long as the tab stayed there.
 */
function AuthedRealtime({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const notificationCopy = useMemo(
    () => ({ title: t('common.notifications.title'), body: t('common.notifications.desktopBody') }),
    [t],
  );

  return (
    <RealtimeProvider
      enabled={isAuthenticated}
      currentUserId={user?.id}
      notificationCopy={notificationCopy}
    >
      {children}
    </RealtimeProvider>
  );
}

/**
 * Feeds translated accessible names to the `@munifocus/ui` primitives that
 * render ARIA roles needing one (the loading spinners). Without this they fall
 * back to English, which would announce the French UI in English.
 */
function LocalizedA11yLabels({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const loading = t('common.a11y.loading');
  const labels = useMemo(() => ({ loading }), [loading]);
  return <A11yLabelsProvider value={labels}>{children}</A11yLabelsProvider>;
}

/** Feeds the localized dismiss label into the shared snackbar system. */
function LocalizedSnackbarProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return <SnackbarProvider dismissLabel={t('common.actions.dismiss')}>{children}</SnackbarProvider>;
}

/**
 * Connects the query client's global mutation-failure fallback to the snackbar.
 * See `queryClient`'s `mutationCache` for which failures reach it.
 */
function MutationErrorReporter() {
  const snackbar = useSnackbar();
  useEffect(() => {
    setMutationErrorReporter((error) => snackbar.error(apiErrorMessage(error)));
    return () => setMutationErrorReporter(null);
  }, [snackbar]);
  return null;
}
